import { DescriptorType, FieldListObject } from '@fabric-ui/components';
import { QueryKey } from '@fabric-ui/utils';
import { queryOptions } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { AvContextType } from '../../context/types';
import { getType } from '../../context/useEntities';
import { SqlToFilterConverterColumnType } from '../../utils/filter/SqlToFilterConverter/SqlToFilterConverter';

const typeMap = {
  [DescriptorType.TYPE_BOOL]: 'boolean',
  [DescriptorType.TYPE_DOUBLE]: 'number',
  [DescriptorType.TYPE_FLOAT]: 'number',
  [DescriptorType.TYPE_ENUM]: 'string',
  [DescriptorType.TYPE_FIXED32]: 'number',
  [DescriptorType.TYPE_FIXED64]: 'number',
  [DescriptorType.TYPE_INT32]: 'number',
  [DescriptorType.TYPE_INT64]: 'number',
  [DescriptorType.TYPE_STRING]: 'string',
  [DescriptorType.TYPE_UINT32]: 'number',
  [DescriptorType.TYPE_UINT64]: 'number',
  [DescriptorType.TYPE_SFIXED32]: 'number',
  [DescriptorType.TYPE_SFIXED64]: 'number',
  [DescriptorType.TYPE_SINT32]: 'number',
  [DescriptorType.TYPE_SINT64]: 'number',
  [DescriptorType.TYPE_GROUP]: 'string',
  [DescriptorType.TYPE_MESSAGE]: 'number',
  [DescriptorType.TYPE_BYTES]: 'number',
  MESSAGE: 'string',
  DATETIME: 'date',
} as const satisfies Record<DescriptorType | 'MESSAGE' | 'DATETIME', SqlToFilterConverterColumnType>;

export const createFirstPartySearchLogsSql = (projectionId?: string | null, fields?: string[] | null, limit = 100, offset = 0) =>
  Array.isArray(fields) && projectionId
    ? [
        'SELECT',
        fields.map(field => `${projectionId}.${field} AS ${field}`).join(', '),
        'FROM',
        projectionId,
        'OFFSET',
        offset,
        'LIMIT',
        limit,
      ].join(' ')
    : null;

export const createFirstPartySearchLogsTotalsSql = (projectionId: string) =>
  `SELECT count(${projectionId}.event_timestamp) as totals FROM ${projectionId}`;

export const createFirstPartyLogsPieSql = (projectionId?: string | null, field?: string | null, _alias?: string, top: number = 100) => {
  const normalizedField = field?.replace(`${projectionId}.`, '');
  const alias = (_alias || normalizedField || '').replace(`${projectionId}.`, '');
  return projectionId && normalizedField
    ? `SELECT ${projectionId}.${normalizedField} AS ${alias}, COUNT(${projectionId}.${normalizedField}) as count FROM ${projectionId} GROUP BY ${alias} ORDER BY count DESC LIMIT ${top}`
    : '';
};

export type ViewProjections = {
  displayName: string;
  name: string;
  builtIn: boolean;
  number: any;
  repeated: boolean;
  type: number | string;
  originalType: { kind: string };
  id: string;
  value: string;
  title: string;
  columnMap: SqlToFilterConverterColumnType;
} & Pick<FieldListObject, 'mainProjId'>;

// TODO: [AVA-21346] useGQLQuery hook
export const getViewProjectionsFieldQuery = ({ api }: { api: AvContextType['api'] }) =>
  queryOptions({
    queryKey: [QueryKey.GetViewProjections],
    gcTime: 1_000 * 60 * 5,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_VIEW_PROJECTIONS, { signal }).then(
        res =>
          res?.data?.viewProjections?.reduce(
            (acc, { projId, fields }) => ({
              ...acc,
              [projId.name]: fields.map(field => {
                const columnMap = typeMap[getType(field.type, field.repeated) as keyof typeof typeMap];
                if (!columnMap) {
                  console.warn(`Unknown type for field ${field.name} in projection ${projId.name}`, {
                    ...field,
                    getTypeResult: getType(field.type, field.repeated),
                  });
                }

                const vp: ViewProjections = {
                  ...field,
                  originalType: field.type,
                  type: getType(field.type, field.repeated),
                  id: `${projId.name}.${field.name}`,
                  value: `${projId.name}.${field.name}`,
                  title: field.displayName || field.name,
                  columnMap: columnMap || 'string',
                  mainProjId: projId,
                };

                return vp;
              }),
            }),
            {}
          ) as Record<string, ViewProjections[]>
      ) as Promise<Record<string, ViewProjections[]>>,
  });

const GET_VIEW_PROJECTIONS = gql`
  query viewProjections {
    viewProjections {
      projId {
        name
        builtIn
      }
      fields {
        displayName
        name
        builtIn
        number
        repeated
        type
      }
    }
  }
`;
